import { Core } from "@faire/design-tokens";
import { Row } from "@faire/web/slate/Layout";
import { TypographyStyles } from "@faire/web/slate/Typography";
import React, { useRef, useEffect, useCallback } from "react";
import styled from "styled-components";

const RETURN_KEY = 13;
const ESCAPE_KEY = 27;
const KEY_EVENT_VALUE = "keydown";

interface HTMLInputTextElement extends HTMLElement {
  value: string;
}

export interface ISearchInputInnerProps<T extends HTMLInputTextElement> {
  id: string;
  value: string;
  onChange: (event: React.ChangeEvent<T>) => void;
  onFocus: (event: React.FocusEvent<T>) => void;
  onKeyDown: (event: React.KeyboardEvent<T>) => void;
  placeholder: string;
  "aria-label"?: string;
  ref: React.RefCallback<T>;
  autoComplete?: string;
  "data-test-id": string;
}

/** Props for the input component. */
export interface ISearchInputProps<T extends HTMLInputTextElement> {
  /** The component to use as the underlying input type. */
  SearchInputInnerComponent: React.ComponentType<ISearchInputInnerProps<T>>;

  id: string;
  inputDataTestId: string;
  searchValue: string;
  placeholder: string;

  onSearchChange: (search: string, timeStamp?: number) => void;
  onReturnPressed: () => void;
  onEscapePressed: () => void;

  onFocus: (event: React.FocusEvent<T>) => void;

  onClick: (event: React.MouseEvent<T>) => void;
  onHover: () => void;
  getRef: (element: T | null) => void;
  "aria-label"?: string;
}

/**
 * A generic search input text component that can be used with different underlying input types,
 * such as `input` and `textarea`.
 *
 * The underlying input type component is passed as a `SearchInputInnerComponent`.
 */
export const SearchInputBase = <T extends HTMLInputTextElement>({
  SearchInputInnerComponent,
  searchValue,
  onClick,
  onSearchChange,
  onFocus,
  onReturnPressed,
  onEscapePressed,
  onHover,
  placeholder,
  id,
  getRef,
  inputDataTestId,
  "aria-label": ariaLabel,
}: ISearchInputProps<T>) => {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<T>) => {
      if (
        event.type !== KEY_EVENT_VALUE ||
        ![RETURN_KEY, "Enter", ESCAPE_KEY, "Escape"].includes(
          event.keyCode || event.key
        )
      ) {
        return;
      }

      event.preventDefault();
      switch (event.keyCode || event.key) {
        case RETURN_KEY:
        case "Enter":
          onReturnPressed();
          break;
        case ESCAPE_KEY:
        case "Escape":
          onEscapePressed();
          break;
        default:
          break;
      }
    },
    [onEscapePressed, onReturnPressed]
  );

  // If an input component with the autoComplete value 'username' is not provided, Chrome's autofill will just fill the first input field it finds.
  // The working solution is to provide a hidden username field to guard against any unwanted values.
  const hiddenInputField = (
    <input
      name="auto-fill guard"
      type="username"
      style={{ display: "none" }}
      autoComplete="username"
    />
  );

  const inputRef = useRef<T | null>(null);
  const isMounted = useRef<boolean>(false);

  // Handle case where user has started to enter text during SSR by updating state
  // on hydration.
  useEffect(() => {
    if (isMounted.current || !inputRef.current) {
      return;
    }
    isMounted.current = true;
    const domValue = inputRef.current?.value;
    if (domValue) {
      onSearchChange(domValue);
    }
  }, [onSearchChange]);

  const handleSearchChange = useCallback(
    (ev: React.ChangeEvent<T>) => {
      onSearchChange(ev.target.value, ev.timeStamp);
    },
    [onSearchChange]
  );

  return (
    <Wrapper onClick={onClick} onMouseOver={onHover}>
      {hiddenInputField}
      <SearchInputInnerComponent
        id={id}
        value={searchValue}
        onChange={handleSearchChange}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        aria-label={ariaLabel}
        ref={(ref) => {
          getRef(ref);
          inputRef.current = ref;
        }}
        autoComplete="off"
        data-test-id={inputDataTestId}
      />
    </Wrapper>
  );
};

/**
 * A search input component that uses an `input` element as the underlying input type.
 */
export const SearchInput: React.FC<
  Omit<ISearchInputProps<HTMLInputTextElement>, "SearchInputInnerComponent">
> = (props) => {
  return <SearchInputBase {...props} SearchInputInnerComponent={StyledInput} />;
};

const Wrapper = styled(Row)`
  position: relative;
  width: 100%;
`;

/**
 * @trackcomponent
 * @supersededBy "@faire/web/slate-deprecated/SearchInput/SearchInput#SearchInput"
 */
const StyledInput = styled.input`
  ${TypographyStyles({ as: undefined })}
  padding-left: 20px;
  background-color: unset;
  width: 100%;
  border: 0;

  &::placeholder {
    color: ${Core.text.subdued};
  }
`;
